import { useCallback, useEffect } from "react";
import api from "../../utils/api";
import { apiUrls } from "../../utils/urls";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((character) => character.charCodeAt(0)));
}

async function savePushSubscription(subscription) {
  await api.post(apiUrls.push.subscribe(), subscription.toJSON());
}

export default function usePushNotifications({ enabled }) {
  const supportsPushNotifications = useCallback(() => {
    return (
      process.env.NODE_ENV === "production" &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      typeof Notification !== "undefined" &&
      Boolean(process.env.REACT_APP_VAPID_PUBLIC_KEY)
    );
  }, []);

  const syncExistingSubscription = useCallback(async () => {
    if (!supportsPushNotifications()) return false;
    if (Notification.permission !== "granted") return false;

    const registration = await navigator.serviceWorker.ready;
    const existingSubscription = await registration.pushManager.getSubscription();
    if (!existingSubscription) return false;

    await savePushSubscription(existingSubscription);
    return true;
  }, [supportsPushNotifications]);

  const requestPermissionAndSubscribe = useCallback(async () => {
    if (!supportsPushNotifications()) {
      return { status: "unsupported" };
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        await savePushSubscription(existingSubscription);
        return { status: "already_subscribed" };
      }

      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        return { status: permission === "denied" ? "denied" : "dismissed" };
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          process.env.REACT_APP_VAPID_PUBLIC_KEY
        ),
      });
      await savePushSubscription(subscription);
      return { status: "subscribed" };
    } catch (error) {
      console.error("Failed to subscribe to push notifications:", error);
      return { status: "error", error };
    }
  }, [supportsPushNotifications]);

  useEffect(() => {
    if (!enabled) return;
    syncExistingSubscription().catch((error) => {
      console.error("Failed to sync existing push subscription:", error);
    });
  }, [enabled, syncExistingSubscription]);

  return {
    supportsPushNotifications: supportsPushNotifications(),
    notificationPermission:
      typeof Notification !== "undefined" ? Notification.permission : "default",
    requestPermissionAndSubscribe,
  };
}
